import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup/ButtonGroup';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { Card } from '@/components/layout/Card/Card';
import { NotificationPreferenceEmailFrequency } from '@/types/schema';

import { NotificationPreferences_NotificationPreferenceFragment } from './graphql/NotificationPreferences.generated';
import { useNotificationPreferenceHandlers } from './hooks/useNotificationPreferenceHandlers';
import { COLUMN_WIDTHS, EMAIL_FREQUENCY_OPTIONS } from './utils';

interface SystemNotificationsCardProps {
  systemPreferences: NotificationPreferences_NotificationPreferenceFragment[];
}

export function SystemNotificationsCard({
  systemPreferences,
}: SystemNotificationsCardProps) {
  const { handleAppEnabledToggle, handleFrequencyChange, saving } =
    useNotificationPreferenceHandlers(systemPreferences);

  // Determine the current state based on all system preferences
  const { appEnabled, emailFrequency } = useMemo(() => {
    if (systemPreferences.length === 0) {
      return {
        appEnabled: false,
        emailFrequency: 'none' as const,
      };
    }

    // All system preferences should have the same state, so we can use the first one
    const firstPref = systemPreferences[0];

    if (!firstPref) {
      return {
        appEnabled: false,
        emailFrequency: 'none' as const,
      };
    }

    return {
      appEnabled: firstPref.appEnabled,
      emailFrequency:
        firstPref.emailEnabled && firstPref.emailFrequency
          ? firstPref.emailFrequency
          : ('none' as const),
    };
  }, [systemPreferences]);

  if (systemPreferences.length === 0) {
    return null;
  }

  return (
    <Card variant="filled-callout" sx={{ p: 1 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ width: COLUMN_WIDTHS.IN_APP }}>
            <Checkbox
              value={appEnabled}
              onChange={handleAppEnabledToggle}
              disabled={saving}
            />
          </Box>

          <Box sx={{ width: COLUMN_WIDTHS.EMAIL_FREQUENCY }}>
            <ButtonGroup<NotificationPreferenceEmailFrequency | 'none'>
              hideLabel
              label="Email frequency for system notifications"
              value={emailFrequency}
              onChange={(_event, value) => handleFrequencyChange(value)}
              disabled={saving}
              options={EMAIL_FREQUENCY_OPTIONS}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            {/* extra nesting so the left-margin doesn't get collapsed into the Stack's spacing */}
            <Box sx={{ ml: 2 }}>
              <Typography variant="body-s">System notifications</Typography>
              <Typography variant="body-xs" display="block" sx={{ mt: 0.5 }}>
                Disconnected integrations, unmapped asset categories, and other
                system-wide errors
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}
